import React, { useEffect } from 'react';
import { ExtensionHeader } from 'extension-header';
import { HeaderIcon, HeaderIconType } from './HeaderIcon.types';
import { HeaderIconElement } from './HeaderIconElement';
import { HeaderProps, TitleJustification } from './Header.types';
import { mergeOverrideStyles } from 'mini-sass-mixins/lib/utils/mergeOverrideStyles';
import { shouldShowEdgeHeader } from 'bootstrap/lib/utils/shouldShowEdgeHeader';
import { trySetFocusOnElement } from 'dom-utils/lib/utils/focusUtils';
import {
    ColorScheme,
    getAccessibleTextColor,
    AccessibleTextSchemeBackground,
} from 'mini-color-utils/lib/ColorScheme';

const styles = require('./Header.scss');
const classNames = require('classnames/bind').bind(styles);

interface FocusProps {
    ref?: React.RefObject<HTMLDivElement>;
}

const tabIndexForItem = (item: string | JSX.Element) => (typeof item === 'string' ? -1 : undefined);

/**
 * Header component to be used in various other components related to calendar event
 * E.g. In create/edit event form, charm combo, reminder combo etc.
 */
function Header(props: HeaderProps, ref?: React.RefObject<HTMLDivElement>): JSX.Element {
    const {
        title,
        secondaryHeaderItem,
        titleStyles,
        secondaryHeaderItemStyles,
        showBorder,
        colorScheme,
        titleAriaLabel,
        secondaryHeaderAriaLabel,
        headerStyles,
        titleJustification = TitleJustification.CENTER,
        headerAriaLabel,
        ariaLevel,
        secondaryTitleRef,
        wrapLeftIconsInContainer = true,
        wrapRightIconsInContainer = true,
        shouldTitleContainerBeAccessible = true,
        hideExtensionHeader = !shouldShowEdgeHeader(),
        hideCalendarBackButton,
        hideMailPeopleBackButton,
        ...extensionHeaderProps
    } = props;
    const leftIcon = props.leftIcon || { icon: '' };
    const rightIcon = props.rightIcon || { icon: '' };
    const backgroundColor = colorScheme ? colorScheme.schemePrimary : null;
    const color = colorScheme
        ? getAccessibleTextColor(colorScheme, AccessibleTextSchemeBackground.schemePrimary)
        : null;

    const headerContainerStyles = {
        [styles.headerContainerRight]: hideCalendarBackButton || hideMailPeopleBackButton,
        [styles.headerContainer]: !hideCalendarBackButton && !hideMailPeopleBackButton,
    };

    const titleContainerStyles = {
        [styles.titleContainer]: !hideCalendarBackButton,
        [styles.titleContainerCenterOneButton]: hideCalendarBackButton,
    };

    const titleFocusRefProps: FocusProps = ref ? { ref } : {};
    const secondaryHeaderFocusRefProps: FocusProps = secondaryTitleRef
        ? { ref: secondaryTitleRef }
        : {};

    const headerRole =
        !title || ariaLevel || headerAriaLabel
            ? { role: 'heading', 'aria-level': ariaLevel || 1, 'aria-label': headerAriaLabel }
            : {};

    const titleContainerAriaProps =
        title && shouldTitleContainerBeAccessible
            ? {
                  role: 'heading',
                  'aria-level': 1,
                  'aria-label': ariaLabelForComponent(title, titleAriaLabel),
                  tabIndex: tabIndexForItem(title),
              }
            : {};

    let titleJustificationStyles;
    switch (titleJustification) {
        case TitleJustification.LEFT:
            titleJustificationStyles = styles.titleLeft;
            break;

        case TitleJustification.RIGHT:
            titleJustificationStyles = styles.titleRight;
            break;

        case TitleJustification.CENTER:
        default:
            titleJustificationStyles = styles.titleCenter;
            break;
    }

    // moving focus to header when it is lazy loaded
    useEffect(() => {
        trySetFocusOnElement(titleFocusRefProps?.ref?.current);
    }, []);

    return (
        <header {...headerRole}>
            {!hideExtensionHeader && <ExtensionHeader {...extensionHeaderProps} />}
            <div
                className={classNames(
                    headerContainerStyles,
                    headerStyles,
                    { showBorder },
                    colorScheme &&
                        mergeOverrideStyles({
                            background: backgroundColor,
                            color: color,
                        })
                )}>
                {renderIcon(
                    leftIcon,
                    false /**isRightIcon */,
                    colorScheme,
                    wrapLeftIconsInContainer
                )}
                {title && (
                    <div className={classNames(titleContainerStyles, titleJustificationStyles)}>
                        {/** Added a key here to enable iOS VO to announce the correct folder name when we switch folder */}
                        <div
                            key={`Folder_Name_${title}`}
                            className={classNames(
                                styles.title,
                                { noSecondaryHeaderItem: !secondaryHeaderItem },
                                titleStyles
                            )}
                            {...titleFocusRefProps}
                            {...titleContainerAriaProps}>
                            {title}
                        </div>
                        {secondaryHeaderItem && (
                            <div
                                {...secondaryHeaderFocusRefProps}
                                className={classNames(
                                    styles.secondaryHeaderItem,
                                    secondaryHeaderItemStyles
                                )}
                                aria-label={ariaLabelForComponent(
                                    secondaryHeaderItem,
                                    secondaryHeaderAriaLabel
                                )}
                                tabIndex={tabIndexForItem(secondaryHeaderItem)}>
                                {secondaryHeaderItem}
                            </div>
                        )}
                    </div>
                )}
                {renderIcon(
                    rightIcon,
                    true /**isRightIcon */,
                    colorScheme,
                    wrapRightIconsInContainer
                )}
            </div>
        </header>
    );
}

const renderIcon = (
    headerIconOrIcons: HeaderIconType,
    isRightIcon: boolean,
    colorScheme?: ColorScheme,
    wrapInContainer?: boolean
): JSX.Element => {
    if (Array.isArray(headerIconOrIcons)) {
        const innerContent: JSX.Element[] = headerIconOrIcons.map(
            (headerIcon: HeaderIcon, index: number) =>
                headerIcon && (
                    <HeaderIconElement
                        {...headerIcon}
                        isRightIcon={isRightIcon}
                        headerColorScheme={colorScheme}
                        key={`HeaderIcon_${index}`}
                        isInArray
                        ref={headerIcon.iconRef}
                        wrapIconInContainer={wrapInContainer}
                    />
                )
        );
        return wrapInContainer ? (
            <div className={styles.multipleIconsContainer}>{innerContent}</div>
        ) : (
            <>{innerContent}</>
        );
    } else {
        const headerIcon = headerIconOrIcons as HeaderIcon; // This line is just for readability, grammatical correctness and semantic clarity
        if (!headerIcon.icon) {
            return null;
        }

        return (
            <HeaderIconElement
                {...headerIcon}
                ref={headerIcon.iconRef}
                isRightIcon={isRightIcon}
                headerColorScheme={colorScheme}
                wrapIconInContainer={wrapInContainer}
            />
        );
    }
};

const ariaLabelForComponent = (component: string | JSX.Element, propAriaLabel: string): string =>
    propAriaLabel ? propAriaLabel : typeof component === 'string' ? component : undefined;

export default React.forwardRef(Header);
